import React, { useEffect, useRef } from 'react'
import { X, ExternalLink, Clapperboard } from 'lucide-react'
import usePlayerStore from '../stores/playerStore'
import { sourceLink } from './docFormat'
import { canEmbed, embedPlatform, embedShape, embedUrl, tiktokCommand } from './videoEmbed'

// "Watch the source" completed in-app: an embedded player that opens the source
// video AT the referenced second, instead of bouncing the user out to a new tab.
// Which platforms embed (and how) lives in ./videoEmbed — this file is the
// modal chrome. Sources without an embed (local/drive uploads, audio_url) never
// reach the frame: callers fall back to the external link.

const platformName = (origin) => {
  const o = String(origin || '').toLowerCase()
  if (o.startsWith('youtube')) return 'YouTube'
  if (o === 'rutube') return 'Rutube'
  if (o === 'tiktok') return 'TikTok'
  return 'site'
}

const fmtTime = (s) => {
  const t = Math.max(0, Math.floor(s || 0))
  const h = Math.floor(t / 3600)
  const m = Math.floor((t % 3600) / 60)
  const sec = String(t % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

export default function VideoPlayerModal() {
  const video = usePlayerStore((s) => s.video)
  const closePlayer = usePlayerStore((s) => s.closePlayer)
  const frameRef = useRef(null)
  const isTikTok = embedPlatform(video) === 'tiktok'

  useEffect(() => {
    if (!video) return undefined
    const onKey = (e) => e.key === 'Escape' && closePlayer()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [video, closePlayer])

  // TikTok drives seek and audio through postMessage, not the embed URL: its
  // `timestamp` param toggles the time DISPLAY, and an autoplaying embed comes
  // up muted whenever the browser refuses unmuted autoplay. So once the player
  // announces itself, jump to the cited second and turn the sound on.
  useEffect(() => {
    if (!video || !isTikTok) return undefined
    const onMessage = (e) => {
      if (e.source !== frameRef.current?.contentWindow) return
      let d = e.data
      if (typeof d === 'string') {
        try { d = JSON.parse(d) } catch { return }
      }
      if (d?.type !== 'onPlayerReady') return
      const w = frameRef.current?.contentWindow
      const start = Math.floor(video.start || 0)
      if (start > 0) tiktokCommand(w, 'seekTo', start)
      tiktokCommand(w, 'unMute')
      tiktokCommand(w, 'play')
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [video, isTikTok])

  if (!video) return null
  const src = embedUrl(video, video.start)
  const external = sourceLink(video, video.start)
  const portrait = embedShape(video) === 'portrait'

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8 bg-midnight-900/70 backdrop-blur-sm"
      onClick={closePlayer}
    >
      <div
        className={`w-full ${portrait ? 'max-w-sm' : 'max-w-4xl'} rounded-xl border border-gold/40 bg-parchment-50 shadow-2xl overflow-hidden dark:border-gold/30 dark:bg-midnight-800`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-3 px-5 py-3 border-b border-parchment-border dark:border-midnight-600">
          <Clapperboard className="w-4 h-4 shrink-0 text-gold-deep dark:text-gold" />
          <div className="min-w-0 flex-1">
            <div className="font-display text-base text-ink dark:text-mist truncate" title={video.title}>
              {video.title || 'Source video'}
            </div>
            {video.start != null && (
              <div className="text-[11px] text-ink-soft dark:text-mist/50">
                from {fmtTime(video.start)} · {video.origin || 'video'}
              </div>
            )}
          </div>
          {external && (
            <a
              href={external}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border border-parchment-border text-ink-soft hover:border-gold hover:text-gold-deep dark:border-midnight-600 dark:text-mist/60 dark:hover:text-gold"
            >
              <ExternalLink className="w-3.5 h-3.5" /> open on {platformName(video.origin)}
            </a>
          )}
          <button
            onClick={closePlayer}
            className="p-2 rounded-md text-ink-soft hover:bg-parchment-200 dark:text-mist/60 dark:hover:bg-midnight-700"
            aria-label="Close player"
          >
            <X className="w-4 h-4" />
          </button>
        </header>
        {src ? (
          // Portrait clips size off the VIEWPORT HEIGHT (aspect-ratio alone
          // would stretch the 9:16 frame to the modal width and overflow tall
          // screens); landscape keeps the plain 16:9 box.
          <div className={`bg-black ${portrait ? 'flex justify-center' : 'aspect-video'}`}>
            <iframe
              ref={frameRef}
              src={src}
              title={video.title || 'Source video'}
              className={portrait ? 'aspect-[9/16] h-[70vh] max-h-[683px]' : 'w-full h-full'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-ink-soft dark:text-mist/60">
            This source has no embeddable player — use the external link above.
          </div>
        )}
      </div>
    </div>
  )
}

// Re-exported so existing callers keep importing the player's capability check
// from the player module.
export { canEmbed, embedUrl }
